/**
 * QuestionBlock — the data contract for the single-choice `ask_user` picker.
 *
 * Used for entity disambiguation, scope clarification, and any other
 * question where the LLM needs the user to pick one of 2–8 labeled options.
 *
 * Pure TypeScript file with zero framework imports.
 * Backend tool schema (Zod) should mirror these types.
 */

export interface QuestionOption {
  id: string;
  label: string;
  subtitle?: string | null;
  metadata?: Record<string, string> | null;
}

export interface QuestionBlock {
  prompt: string;
  options: QuestionOption[];
}

export function isValidQuestionBlock(data: unknown): data is QuestionBlock {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  if (typeof d.prompt !== 'string' || d.prompt.length === 0) return false;
  if (!Array.isArray(d.options) || d.options.length < 2 || d.options.length > 8) return false;
  return d.options.every((o: unknown) => {
    if (!o || typeof o !== 'object') return false;
    const opt = o as Record<string, unknown>;
    return (
      typeof opt.id === 'string' &&
      opt.id.length > 0 &&
      typeof opt.label === 'string' &&
      opt.label.length > 0
    );
  });
}

/**
 * Custom AI SDK UIMessage data part emitted on the user message when the
 * user answers an ask_user question. Carries the structured answer so it
 * round-trips through message.metadata.parts and survives thread reload.
 */
export interface UserAnswerPart {
  type: 'data-user-answer';
  data: {
    toolCallId: string;
    answerId: string;
    answerLabel: string;
  };
}

export function isUserAnswerPart(part: unknown): part is UserAnswerPart {
  if (!part || typeof part !== 'object') return false;
  const p = part as Record<string, unknown>;
  if (p.type !== 'data-user-answer') return false;
  const d = p.data as Record<string, unknown> | undefined;
  return (
    !!d &&
    typeof d.toolCallId === 'string' &&
    typeof d.answerId === 'string' &&
    typeof d.answerLabel === 'string'
  );
}
